import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';
import { CONSENT_EVENT, readConsent, saveConsent } from '../lib/consent';

const CATEGORIES = [
  {
    key: 'analytics',
    label: 'Analytics cookies',
    body: 'Google Analytics — how many people visit and which pages they use, so we can improve the site.'
  },
  {
    key: 'marketing',
    label: 'Advertising cookies',
    body: 'Google Ads — measures which adverts lead to enquiries and lets us show relevant ads.'
  }
];

const Toggle = ({ checked, onChange, label, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-200 ${
      checked ? 'bg-emerald-500' : 'bg-white/20'
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const CookieConsent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    // Not rendered during react-snap prerender, so the static HTML matches the
    // first client render (avoids a hydration mismatch)
    if (navigator.userAgent === 'ReactSnap') return undefined;
    const stored = readConsent();
    if (stored) {
      setPrefs(stored);
      setHasChoice(true);
      return undefined;
    }
    const timer = setTimeout(() => setIsOpen(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const reopen = () => {
      const stored = readConsent();
      setPrefs(stored || { analytics: false, marketing: false });
      setHasChoice(!!stored);
      setShowDetail(true);
      setIsOpen(true);
    };
    window.addEventListener(CONSENT_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_EVENT, reopen);
  }, []);

  const commit = useCallback((choice) => {
    saveConsent(choice);
    setPrefs(choice);
    setHasChoice(true);
    setIsOpen(false);
    setShowDetail(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-[#0f1729] border border-white/10 rounded-2xl shadow-2xl shadow-black/40 pointer-events-auto overflow-hidden">
        <div className="px-5 sm:px-7 py-5 sm:py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-base md:text-lg">Your cookie choices</h2>
              <p className="text-sm text-gray-300 leading-relaxed mt-1.5">
                We use essential cookies to make this site work. With your permission we&apos;d also
                like to use analytics and advertising cookies. Nothing optional is set until you
                agree, and you can change your mind at any time.{' '}
                <Link
                  to="/privacy"
                  className="text-emerald-400 font-medium underline decoration-dotted"
                  data-testid="cookie-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            {hasChoice && (
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close cookie settings"
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                data-testid="cookie-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {showDetail && (
            <div className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="cookie-preferences-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-white">Essential cookies</p>
                  <p className="text-xs text-gray-400 leading-relaxed mt-0.5">
                    Needed for the site to work. These are always on.
                  </p>
                </div>
                <span className="text-xs text-gray-400 font-medium flex-shrink-0 mt-1">Always on</span>
              </div>
              {CATEGORIES.map((cat) => (
                <div key={cat.key} className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-white">{cat.label}</p>
                    <p className="text-xs text-gray-400 leading-relaxed mt-0.5">{cat.body}</p>
                  </div>
                  <Toggle
                    checked={prefs[cat.key]}
                    onChange={(value) => setPrefs((p) => ({ ...p, [cat.key]: value }))}
                    label={cat.label}
                    testId={`cookie-toggle-${cat.key}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2.5 mt-5">
            <Button
              onClick={() => commit({ analytics: true, marketing: true })}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#0f1729] font-semibold rounded-full"
              data-testid="cookie-accept-all-btn"
            >
              Accept all
            </Button>
            <Button
              onClick={() => commit({ analytics: false, marketing: false })}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full border border-white/15"
              data-testid="cookie-reject-all-btn"
            >
              Reject all
            </Button>
            {showDetail ? (
              <Button
                onClick={() => commit(prefs)}
                variant="ghost"
                className="flex-1 text-gray-300 hover:text-white hover:bg-white/10 rounded-full"
                data-testid="cookie-save-preferences-btn"
              >
                Save my choices
              </Button>
            ) : (
              <Button
                onClick={() => setShowDetail(true)}
                variant="ghost"
                className="flex-1 text-gray-300 hover:text-white hover:bg-white/10 rounded-full"
                data-testid="cookie-manage-btn"
              >
                Manage cookies
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
